/** @fileoverview Renders the empty chat welcome view and starter prompts. */

import { Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import type { ChatWelcomeProps } from './types';

/* Each example shows a short label; the click puts the full prompt in the composer.
   Every prompt maps to something the builder can do today: a system list, a detail
   screen with a checklist button, an Excel export, or an email to task owners. */
const examples = [
  {
    label: "Show HR this year's new joiners from SuccessFactors",
    prompt:
      'Build an app for HR that lists the employees who joined this year from SuccessFactors Employee Central, showing name, start date, department and employee ID, with the newest joiners first.',
  },
  {
    label: 'Let managers open a leaver and start the offboarding checklist',
    prompt:
      'Build an app for managers that lists employees leaving this year from SuccessFactors. Clicking an employee opens their details (name, termination date, last working day) and a "Start offboarding checklist" button that adds the standard tasks: collect equipment, remove system access, handover, exit interview.',
  },
  {
    label: "List this year's leavers with an Excel download button",
    prompt:
      'Build an app for HR that lists employees leaving this year from SuccessFactors, with name, termination date and employee ID, and a button that downloads the list as an Excel file.',
  },
  {
    label: 'A task list that emails each owner their open tasks',
    prompt:
      'Build a team task tracker: a list of projects, each with a checklist of tasks that have an owner and a due date, and a button that emails every owner their open tasks, one email per person.',
  },
];

export default function ChatWelcome({
  composer,
  onSelectExample,
  onOpenBuilder,
}: ChatWelcomeProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 px-7 pb-24 pt-32 text-center @max-5xl:max-w-none @max-5xl:flex-1 @max-5xl:px-3 @max-5xl:pt-0 @max-5xl:pb-2 @max-5xl:text-left">
        {/* Narrow layout keeps the prompt at the bottom; the logo and heading fill the open space above it. */}
        <div className="mb-3 @max-5xl:mb-2 @max-5xl:flex @max-5xl:min-h-24 @max-5xl:flex-1 @max-5xl:flex-col @max-5xl:items-center @max-5xl:justify-center @max-5xl:px-1 @max-5xl:text-center">
          <Sparkles
            className="mx-auto mb-5 h-7 w-7 text-app-brand-strong @max-5xl:mb-3 @max-5xl:h-8 @max-5xl:w-8"
            aria-hidden="true"
          />
          <h1 className="text-3xl font-semibold tracking-tight text-app-text @max-5xl:text-xl">
            What should this app do?
          </h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-balance text-app-text-muted @max-5xl:mt-1 @max-5xl:max-w-xs @max-5xl:text-pretty">
            Describe it in a sentence. You will get a few quick questions, then a plan to approve.
          </p>
        </div>
        <div className="@max-5xl:order-2">{composer}</div>
        <div className="text-center @max-5xl:order-1 @max-5xl:w-full @max-5xl:min-w-0 @max-5xl:text-left">
          <p className="text-xs font-bold tracking-wide text-app-text-faint uppercase @max-5xl:px-1">
            For example
          </p>
          <div className="mt-2 flex flex-col items-center gap-2 @max-5xl:items-stretch">
            {examples.map(({ label, prompt }) => (
              <Button
                variant="outline"
                className="h-auto max-w-full rounded-full border-app-border-strong bg-app-surface-card px-4 py-1.5 text-xs font-medium text-app-text-secondary hover:border-app-brand-strong @max-5xl:w-full @max-5xl:min-w-0 @max-5xl:justify-start @max-5xl:rounded-lg @max-5xl:whitespace-normal @max-5xl:text-left @max-5xl:wrap-break-word"
                key={label}
                type="button"
                title={prompt}
                onClick={() => onSelectExample(prompt)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        {onOpenBuilder && (
          <div className="mt-4 flex justify-center gap-8 text-xs text-app-text-subtle @max-5xl:hidden">
            <Button
              variant="link"
              className="h-auto p-0 text-xs text-app-text-subtle underline underline-offset-2"
              type="button"
              onClick={onOpenBuilder}
            >
              Start with a blank canvas
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
